/**
 * Date Calculation Utilities
 * Functions for date arithmetic and time calculations
 */

import dayjs, { type Dayjs, type ManipulateType } from 'dayjs';

// Type for dayjs input - matches what dayjs() constructor accepts
type DayjsInput = string | number | Date | Dayjs | null | undefined;

/**
 * Get current time as a dayjs object
 * Replaces new Date() and Date.now() usage
 */
export function now(): Dayjs {
  return dayjs();
}

/**
 * Get current Unix timestamp in milliseconds
 * Replaces Date.now() usage
 */
export function nowTimestamp(): number {
  return dayjs().valueOf();
}

/**
 * Get current Unix timestamp in seconds
 */
export function nowTimestampSeconds(): number {
  return Math.floor(dayjs().valueOf() / 1000);
}

/**
 * Add time to a date
 * @param date - Base date
 * @param amount - Amount to add
 * @param unit - Unit of time (e.g., 'day', 'hour', 'minute', 'second')
 */
export function addTime(date: DayjsInput, amount: number, unit: ManipulateType): Dayjs {
  return dayjs(date).add(amount, unit);
}

/**
 * Subtract time from a date
 * @param date - Base date
 * @param amount - Amount to subtract
 * @param unit - Unit of time (e.g., 'day', 'hour', 'minute', 'second')
 */
export function subtractTime(date: DayjsInput, amount: number, unit: ManipulateType): Dayjs {
  return dayjs(date).subtract(amount, unit);
}

/**
 * Get the difference between two dates in a specific unit
 * @param date1 - First date
 * @param date2 - Second date
 * @param unit - Unit for the difference (e.g., 'millisecond', 'second', 'minute', 'hour', 'day')
 * @returns Difference in the specified unit
 */
export function getTimeDifference(
  date1: DayjsInput,
  date2: DayjsInput,
  unit: 'millisecond' | 'second' | 'minute' | 'hour' | 'day' = 'millisecond',
): number {
  const d1 = dayjs(date1);
  const d2 = dayjs(date2);
  return d1.diff(d2, unit);
}

/**
 * Get Unix timestamp in milliseconds from a date
 * Replaces .getTime() calls
 */
export function getUnixTimestamp(date: DayjsInput): number {
  return dayjs(date).valueOf();
}

/**
 * Get Unix timestamp in seconds from a date
 */
export function getUnixTimestampSeconds(date: DayjsInput): number {
  return Math.floor(dayjs(date).valueOf() / 1000);
}
